import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Minus, MessageCircleQuestion } from "lucide-react";
import { Helmet } from "react-helmet-async";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function FAQs() {
  const [faqs, setFaqs] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/faq`);
        setFaqs(res.data.filter((t) => t.isVerified));
      } catch (err) {
        console.error("Failed to fetch FAQs:", err);
      }
    };

    fetchFAQs();
  }, []);

  const toggle = (index) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  return (
    <div className="min-h-screen bg-white px-6 py-20 lg:py-24 font-sans text-black mt-16">
      <Helmet>
        <title>FAQs | Pak Fashions</title>
        <meta
          name="description"
          content="Find answers to the most frequently asked questions about Pak Fashions' products, shipping, orders, and more."
        />
        <meta
          name="keywords"
          content="Pak Fashions FAQs, fashion questions, shipping info, order help, clothing support"
        />
        <meta name="author" content="Pak Fashions" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content="FAQs | Pak Fashions" />
        <meta
          property="og:description"
          content="Common questions answered about shopping, shipping, and support from Pak Fashions."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pakfashions.co.ke/faqs" />
        <meta
          property="og:image"
          content="https://pakfashions.co.ke/pak-fashions.png"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FAQs | Pak Fashions" />
        <meta
          name="twitter:description"
          content="Get help with your orders, shipping, and more from our FAQ section."
        />
        <meta
          name="twitter:image"
          content="https://pakfashions.co.ke/pak-fashions.png"
        />
      </Helmet>

      {/* Hero Section */}
      <div className="max-w-4xl mx-auto text-center mb-16 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-yellow-100 rounded-full blur-3xl opacity-50 pointer-events-none"></div>
        <div className="relative z-10">
          <MessageCircleQuestion className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight">
            Frequently Asked <span className="text-yellow-500">Questions</span>
          </h1>
          <p className="text-gray-500 text-lg md:text-xl max-w-2xl mx-auto font-medium leading-relaxed">
            Everything you need to know about our products, shipping, and how we
            deliver luxury to your doorstep.
          </p>
          <div className="w-24 h-1 bg-yellow-400 mx-auto mt-8 rounded-full shadow-sm" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto space-y-5">
        {faqs.length === 0 && (
          <div className="text-center py-20 bg-gray-50 rounded-3xl border border-gray-100">
            <p className="text-gray-500 font-medium">
              Check back later for answers to common questions!
            </p>
          </div>
        )}

        {faqs.map((faq, index) => {
          const isOpen = activeIndex === index;
          return (
            <div
              key={faq.id}
              className={`bg-white border rounded-[2rem] transition-all duration-300 overflow-hidden ${
                isOpen
                  ? "border-yellow-200 shadow-lg shadow-yellow-100/50"
                  : "border-gray-100 shadow-sm hover:border-yellow-200 hover:shadow-md"
              }`}
            >
              <button
                onClick={() => toggle(index)}
                className={`w-full flex justify-between items-center text-left p-6 md:p-8 transition-colors duration-300 focus:outline-none ${
                  isOpen ? "bg-yellow-50/50" : "bg-white hover:bg-gray-50/50"
                }`}
              >
                <span
                  className={`text-xl font-bold pr-8 transition-colors duration-300 ${isOpen ? "text-gray-900" : "text-gray-700"}`}
                >
                  {faq.question}
                </span>
                <div
                  className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen ? "bg-yellow-400 text-white shadow-sm" : "bg-gray-100 text-gray-500"}`}
                >
                  {isOpen ? (
                    <Minus className="w-5 h-5" />
                  ) : (
                    <Plus className="w-5 h-5" />
                  )}
                </div>
              </button>

              <div
                className={`transition-all duration-500 ease-in-out ${
                  isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="p-6 md:p-8 pt-0 border-t border-yellow-100/50 bg-yellow-50/50 text-gray-600 leading-relaxed text-lg">
                  <div className="pt-6">{faq.answer}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Contact CTA */}
      <div className="max-w-3xl mx-auto mt-20 text-center bg-gray-900 rounded-[3rem] p-12 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-500 rounded-full blur-[100px] opacity-20 transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="relative z-10">
          <h2 className="text-3xl font-bold text-white mb-4 tracking-tight">
            Still have questions?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-lg mx-auto">
            Can't find the answer you're looking for? Please chat to our
            friendly team.
          </p>
          <a
            href="/contact"
            className="inline-block bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold py-4 px-10 rounded-full shadow-lg transition-all duration-300 hover:-translate-y-1"
          >
            Get in touch
          </a>
        </div>
      </div>
    </div>
  );
}

export default FAQs;
